/**
 * Centralized color definitions for the Enlighten application
 *
 * This file contains all color definitions used throughout the application.
 * Always import colors from this file instead of hardcoding them in components.
 */

// Basic color type definition
import { IndicatorFields, Relevance, Risk, Sentiment } from "../types/artifact";

export type Color = string;

// Color with text color for contrast
export type ColorWithText = {
  background: Color;
  text: Color;
};

// Chip color type with background, border and text color
export type ChipColor = {
  background: Color;
  border: Color;
  fontColor: Color;
};

// ===== Base Colors =====

// Primary colors
export const PRIMARY = {
  main: "#1976D2",
  light: "#42A5F5",
  dark: "#1565C0"
};

// Secondary colors
export const SECONDARY = {
  main: "#9C27B0",
  light: "#BA68C8",
  dark: "#7B1FA2"
};

// Background colors
export const BACKGROUND = {
  default: "#F5F5F5",
  paper: "#FFFFFF"
};

// ===== Semantic Colors =====

// Status colors
export const STATUS = {
  success: "#4CAF50", // Green
  warning: "#FFB300", // Yellow/Amber
  error: "#F44336", // Red
  info: "#2196F3", // Blue
  grey: "#757575" // Grey
};

// Text colors
export const TEXT = {
  primary: "#000000",
  secondary: "#757575",
  disabled: "#9E9E9E",
  white: "#FFFFFF",
  black: "#000000"
};

// ===== Component-specific Colors =====

// Chip colors
export const CHIP_COLORS: Record<string, ChipColor> = {
  red: { background: "#F44336DD", border: "#F44336", fontColor: "#FFFFFF" },
  yellow: { background: "#FFB300DD", border: "#FFB300", fontColor: "#FFFFFF" },
  green: { background: "#4CAF50DD", border: "#4CAF50", fontColor: "#FFFFFF" },
  blue: { background: "#2196F3DD", border: "#2196F3", fontColor: "#FFFFFF" },
  grey: { background: "#F5F5F5", border: "#D0D0D0", fontColor: "#757575" }
};

// Score colors
export const SCORE_COLORS = {
  veryLow: { background: "#FF69B4", border: "#FF69B4", fontColor: "#FFFFFF" }, // Dark pink (0-1)
  low: { background: "#FFC0CB", border: "#FFC0CB", fontColor: "#000000" }, // Light pink (1-2)
  medium: { background: "#FFFACD", border: "#FFFACD", fontColor: "#000000" }, // Light yellow (2-3)
  high: { background: "#90EE90", border: "#90EE90", fontColor: "#000000" }, // Light green (3-4)
  veryHigh: { background: "#66CD00", border: "#66CD00", fontColor: "#FFFFFF" } // Medium green (4-5)
};

export const PERFORMANCE_COLORS = {
  advanced: CHIP_COLORS.green,
  proficient: CHIP_COLORS.blue,
  improving: CHIP_COLORS.yellow,
  needswork: CHIP_COLORS.red,
  na: CHIP_COLORS.grey
};

// Risk colors
export const RISK_COLORS: Record<Risk, ChipColor> = {
  low: CHIP_COLORS.green,
  medium: CHIP_COLORS.yellow,
  high: CHIP_COLORS.red
};

// Relevance colors
export const RELEVANCE_COLORS: Record<Relevance, ChipColor> = {
  high: CHIP_COLORS.green,
  medium: CHIP_COLORS.yellow,
  low: CHIP_COLORS.blue
};

// Sentiment colors
export const SENTIMENT_COLORS: Record<Sentiment, ChipColor> = {
  positive: CHIP_COLORS.green,
  neutral: CHIP_COLORS.yellow,
  negative: CHIP_COLORS.red
};

// Rating colors
export const RATING_COLORS: Record<string, Color> = {
  Excellent: "#4CAF50", // Green
  Exceptional: "#4CAF50", // Green
  "Above Expectations": "#8BC34A", // Light Green
  "Meets Expectations": "#FFEB3B", // Yellow
  "Below Expectations": "#FF9800", // Orange
  Poor: "#F44336" // Red
};

// Avatar colors
export const AVATAR_COLORS: ColorWithText[] = [
  { background: "#E5737366", text: "#E57373" }, // red
  { background: "#64B5F666", text: "#64B5F6" }, // blue
  { background: "#81C78466", text: "#81C784" }, // green
  { background: "#FFB74D66", text: "#FFB74D" }, // orange
  { background: "#BA68C866", text: "#BA68C8" }, // purple
  { background: "#4DB6AC66", text: "#4DB6AC" }, // teal
  { background: "#90A4AE66", text: "#90A4AE" }, // blue-grey
  { background: "#F0629266", text: "#F06292" }, // pink
  { background: "#7986CB66", text: "#7986CB" } // indigo
];

// Histogram colors
export const HISTOGRAM_COLORS = {
  score: [
    "#F44336", // Dark pink (0-1)
    "#FF7043", // Light pink (1-2)
    "#FFEB3B", // Light yellow (2-3)
    "#90EE90", // Light green (3-4)
    "#66CD00" // Medium green (4-5)
  ],
  scoreText: [
    "#FFFFFF", // White text for dark pink
    "#000000", // Black text for light pink
    "#000000", // Black text for light yellow
    "#000000", // Black text for light green
    "#FFFFFF" // White text for medium green
  ],
  risk: [
    "#4CAF50", // Green for low risk
    "#FFB300", // Yellow for medium risk
    "#F44336" // Red for high risk
  ],
  riskText: [
    "#FFFFFF", // White text for green
    "#000000", // Black text for yellow
    "#FFFFFF" // White text for red
  ]
};

export const CHAT_BADGE = {
  dataset: { background: "#E3F2FD", text: "#1565C0" },
  answerMode: { background: "#EDE7F6", text: "#6236BF" }
};

export const CHAT_TOGGLE = {
  dataset: "#5B86B5",
  answerMode: "#6236BF"
};

// AI warning banner colors (amber palette)
export const AI_WARNING = {
  background: "#FEF9EC",
  contentBackground: BACKGROUND.paper,
  border: "#F5D97A",
  textPrimary: "#7A5C0A",
  textSecondary: "#555555",
  sectionHead: "#333333",
  textItalic: "#A08840",
  linkColor: "#A08030",
  footerBorder: "#F0E8CC",
};

// ===== Helper Functions =====

/**
 * Get chip color based on score value
 */
export const getScoreChipColor = (score: number): ChipColor => {
  if (score < 1.5) {
    return SCORE_COLORS.veryLow;
  }
  if (score < 2.5) {
    return SCORE_COLORS.low;
  }
  if (score < 3.5) {
    return SCORE_COLORS.medium;
  }
  if (score < 4.5) {
    return SCORE_COLORS.high;
  }
  return SCORE_COLORS.veryHigh;
};

/**
 * Get chip color for a specific field and value
 */
export const getChipColorForField = (
  field: IndicatorFields,
  value: string | number | null | undefined
): ChipColor => {
  if (value === null || value === undefined) {
    return CHIP_COLORS.grey;
  }
  if (field === "score") {
    if (typeof value === "number") {
      return getScoreChipColor(value);
    }
  }

  if (typeof value !== "string") {
    return CHIP_COLORS.grey;
  }

  const lowercaseValue = value.toLowerCase();

  const fieldColorMaps: Record<string, Record<string, ChipColor>> = {
    risk: RISK_COLORS,
    relevance: RELEVANCE_COLORS,
    sentiment: SENTIMENT_COLORS,
    score: SCORE_COLORS,
    performance: PERFORMANCE_COLORS
  };

  return fieldColorMaps[field]?.[lowercaseValue] || CHIP_COLORS.grey;
};

/**
 * Get rating color based on rating value
 */
export const getRatingColor = (rating: string): Color => {
  return RATING_COLORS[rating] || STATUS.grey;
};

/**
 * Generate a consistent avatar color based on a name
 */
export const getAvatarColor = (name: string): ColorWithText => {
  // Generate a hash code from the name
  let hashCode = 0;
  for (let i = 0; i < name.length; i++) {
    hashCode = name.charCodeAt(i) + ((hashCode << 5) - hashCode);
  }

  // Use the hash to pick a color
  const index = Math.abs(hashCode) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
};

/**
 * Determine text color based on background brightness
 * Returns white for dark backgrounds, black for light ones
 */
export const getContrastTextColor = (backgroundColor: Color): Color => {
  // Simple approach: use white text for darker backgrounds, black for lighter ones
  const hex = backgroundColor.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;

  return brightness > 128 ? TEXT.black : TEXT.white;
};
